import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import type {
  Activity,
  Audience,
  ContactEmail,
  EmailDraft,
  EmailMessage,
  EventWithContext,
  ThreadSummary,
} from '@epasu/core';
import { ArrowLeft, Building2, Handshake, Mail, Pencil, Phone, Trash2, X } from 'lucide-react';
import { crm, type ContactRow, type OrderRow, type TaskRow } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ContactDialog } from '@/components/ContactDialog';
import { DeleteContactDialog } from '@/components/DeleteContactDialog';
import { PromoteCustomerDialog } from '@/components/PromoteCustomerDialog';
import { SetOrganizationDialog } from '@/components/SetOrganizationDialog';
import { MailComposer } from '@/components/MailComposer';
import { ReplyDialog } from '@/components/ReplyDialog';
import { DraftCard } from '@/components/DraftCard';
import { ErstanspracheHinweis } from '@/components/ErstanspracheHinweis';
import { OrderStatusPill } from '@/components/OrderStatusPill';
import { formatEuro } from '@/lib/utils';
import { aktePfad, istLeadAkte } from '@/lib/contact-path';

/**
 * Die Kundenakte.
 *
 * Alles zu einem Kontakt auf einer Seite: Stammdaten, Notiz, offene Aufgaben
 * und der Verlauf. Der Schriftverkehr kommt mit der Mail-Zentrale dazu — bis
 * dahin zeigt der Verlauf, was das System über den Kontakt weiß.
 */
export function ContactDetailPage() {
  const { contactId } = useParams<{ contactId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [contact, setContact] = useState<ContactRow | null>(null);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [threads, setThreads] = useState<ThreadSummary[]>([]);
  const [openThreadId, setOpenThreadId] = useState<string | null>(null);
  const [threadMessages, setThreadMessages] = useState<EmailMessage[]>([]);
  const [drafts, setDrafts] = useState<EmailDraft[]>([]);
  /**
   * Entwürfe, die an keiner Unterhaltung hängen.
   *
   * Ein Entwurf für eine *erste* Mail hat keine Bezugsnachricht und damit auch
   * keinen Thread (`createDraft` leitet ihn nur aus der Bezugsmail ab). Er
   * käme in keiner aufgeklappten Unterhaltung vor — Claude schriebe ins
   * Nichts. Deshalb werden sie hier eigens geladen und unter dem
   * Schriftverkehr gezeigt.
   */
  const [freieEntwuerfe, setFreieEntwuerfe] = useState<EmailDraft[]>([]);
  const [offenerEntwurf, setOffenerEntwurf] = useState<EmailDraft | null>(null);
  const [replying, setReplying] = useState<EmailMessage | null>(null);
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [termine, setTermine] = useState<EventWithContext[]>([]);
  const [audiences, setAudiences] = useState<Audience[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [composing, setComposing] = useState(false);
  const [firmaWaehlen, setFirmaWaehlen] = useState(false);
  const [promoting, setPromoting] = useState(false);

  const load = useCallback(async () => {
    if (!contactId) return;
    const found = await crm.contacts.get(contactId);
    if (!found) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    const [acts, tsk, thr, ord, evt, ent] = await Promise.all([
      crm.activities.list({ contactId, limit: 50 }),
      crm.tasks.list({ contactId }),
      crm.mailbox.threadsForContact(contactId),
      crm.orders.list({ contactId }),
      crm.calendar.events({ contactId, limit: 20 }),
      crm.drafts.list({ contactId }),
    ]);
    setContact(found);
    setActivities(acts);
    setTasks(tsk);
    setThreads(thr);
    setOrders(ord);
    setTermine(evt);
    setFreieEntwuerfe(ent.filter((d) => !d.threadId));
    setLoading(false);
  }, [contactId]);

  /**
   * Entwürfe ohne Unterhaltung nachladen.
   *
   * Eigener Weg statt `load()`: Nach dem Verwerfen eines solchen Entwurfs soll
   * nicht die halbe Akte neu geholt werden.
   */
  const ladeFreieEntwuerfe = useCallback(async () => {
    if (!contactId) return;
    const alle = await crm.drafts.list({ contactId });
    setFreieEntwuerfe(alle.filter((d) => !d.threadId));
  }, [contactId]);

  /**
   * Unterhaltung auf- und zuklappen.
   *
   * Beim Aufklappen gelten die enthaltenen Nachrichten als gelesen — wer sie
   * offen vor sich hat, hat sie gesehen.
   */
  const openThread = useCallback(
    async (threadId: string) => {
      if (openThreadId === threadId) {
        setOpenThreadId(null);
        setThreadMessages([]);
        setDrafts([]);
        return;
      }
      const nachrichten = await crm.mailbox.thread(threadId);
      setOpenThreadId(threadId);
      setThreadMessages(nachrichten);
      setDrafts(await crm.drafts.list({ threadId }));

      const ungelesen = nachrichten.filter((m) => !m.seen && m.direction === 'inbound');
      if (ungelesen.length) {
        await Promise.all(ungelesen.map((m) => crm.mailbox.markSeen(m.id)));
        if (contactId) setThreads(await crm.mailbox.threadsForContact(contactId));
      }
    },
    [contactId, openThreadId],
  );

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void crm.audiences.list().then(setAudiences);
  }, []);

  // Leads und Kontakte teilen sich diese Seite, aber nicht die Adresse (siehe
  // lib/contact-path.ts). Wer aus Posteingang, Kalender oder Aufgaben einen
  // Lead ueber `/kontakte/:id` oeffnet, landet hier und wird auf die
  // Lead-Adresse umgelenkt — mit `replace`, damit „Zurueck“ nicht in einer
  // Schleife zwischen beiden Adressen haengt. Dasselbe in die Gegenrichtung,
  // wenn ein Lead gerade zum Kontakt geworden ist.
  useEffect(() => {
    if (!contact) return;
    const soll = aktePfad(contact.id, contact.lifecycle);
    if (location.pathname !== soll) navigate(soll, { replace: true });
  }, [contact, location.pathname, navigate]);

  const liste =
    contact?.lifecycle === 'lead' || istLeadAkte(location.pathname)
      ? '/akquise?reiter=leads'
      : '/kontakte';

  /**
   * Zurueck dorthin, woher man kam — nicht fest auf eine Liste.
   *
   * Vorher fuehrte „Alle Kontakte“ immer nach `/kontakte`: aus einer Kampagne
   * geoeffnet, landete man danach in der Kontaktuebersicht statt wieder in der
   * Kampagne. Die Liste bleibt der Ausweg, wenn die Seite ohne Vorgeschichte
   * geoeffnet wurde (neuer Tab, Lesezeichen) — `navigate(-1)` verliesse dann die
   * Anwendung.
   *
   * `history.state.idx` fuehrt React Routers Browser-Router selbst; anders als
   * `location.key` bleibt er beim `replace` oben erhalten, sodass eine direkt
   * geoeffnete und umgelenkte Seite nicht faelschlich als „hat Verlauf“ gilt.
   */
  const zurueck = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate(liste);
  };

  if (loading) {
    return <p className="text-sm text-muted-foreground">Lade …</p>;
  }

  if (notFound || !contact) {
    return (
      <Card className="space-y-3 p-8 text-center">
        <p className="text-sm text-muted-foreground">
          Diesen Kontakt gibt es nicht (mehr).
        </p>
        <Button variant="outline" onClick={() => navigate(liste)}>
          Zurück zur Übersicht
        </Button>
      </Card>
    );
  }

  const offeneAufgaben = tasks.filter((t) => t.status !== 'erledigt');

  // Die Bezugsnachricht eines geöffneten Entwurfs: nur gesetzt, wenn sie
  // tatsächlich in der aufgeklappten Unterhaltung steckt. Bei einem Entwurf
  // ohne Bezug oder zu einer anderen Unterhaltung ist sie nicht da — der
  // Dialog öffnet sich dann nicht, statt mit einer nicht vorhandenen Nachricht
  // abzustürzen. Wortgleich zu InboxPage: dieselbe Gefahr, dieselbe Antwort.
  const offenerEntwurfBezug = offenerEntwurf
    ? threadMessages.find((m) => m.id === offenerEntwurf.replyToMessageId)
    : undefined;

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={zurueck}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Zurück
      </button>

      <Card className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-1">
            <h1 className="flex flex-wrap items-center gap-2 text-xl font-semibold">
              {contact.displayName}
              {contact.isCustomer && (
                <Badge variant="success" title={contact.organizationName ?? undefined}>
                  Kunde
                </Badge>
              )}
            </h1>
            <p className="flex flex-wrap items-center gap-x-1.5 text-sm text-muted-foreground">
              {contact.role && <span>{contact.role}</span>}
              {contact.role && <span>·</span>}
              {contact.organizationId ? (
                <>
                  <button
                    className="hover:underline"
                    onClick={() => navigate(`/firmen/${contact.organizationId}`)}
                  >
                    {contact.organizationName}
                  </button>
                  <button
                    className="rounded p-1 hover:bg-muted hover:text-foreground"
                    title="Firma wechseln oder lösen"
                    aria-label="Firma wechseln oder lösen"
                    onClick={() => setFirmaWaehlen(true)}
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                </>
              ) : (
                <button
                  className="inline-flex items-center gap-1.5 rounded-md border border-dashed px-2 py-0.5 text-xs hover:bg-muted hover:text-foreground"
                  onClick={() => setFirmaWaehlen(true)}
                >
                  <Building2 className="h-3.5 w-3.5" />
                  Firma zuordnen
                </button>
              )}
            </p>
            {contact.optedOut && (
              <p className="text-sm text-amber-700">
                Abgemeldet — an diesen Kontakt geht keine Post mehr raus.
              </p>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {contact.email && !contact.optedOut && (
              <Button variant="outline" onClick={() => setComposing(true)}>
                <Mail className="mr-2 h-4 w-4" />
                E-Mail
              </Button>
            )}
            {contact.isCustomer ? (
              <p className="text-sm text-muted-foreground">
                <button
                  type="button"
                  className="font-medium text-foreground hover:underline"
                  onClick={() => navigate(`/firmen/${contact.organizationId}`)}
                >
                  {contact.organizationName}
                </button>{' '}
                ist bereits Kunde.
              </p>
            ) : (
              <Button variant="outline" onClick={() => setPromoting(true)}>
                <Handshake className="mr-2 h-4 w-4" />
                Zum Kunden befördern
              </Button>
            )}
            <Button variant="outline" onClick={() => setEditing(true)}>
              <Pencil className="mr-2 h-4 w-4" />
              Bearbeiten
            </Button>
            <Button variant="ghost" size="icon" aria-label="Kontakt löschen" onClick={() => setDeleting(true)}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        </div>

        <dl className="mt-6 grid gap-4 sm:grid-cols-2">
          <Detail label="E-Mail">
            {contact.email ? (
              <a className="hover:underline" href={`mailto:${contact.email}`}>
                {contact.email}
              </a>
            ) : (
              '—'
            )}
            <Zweitadressen contactId={contact.id} />
          </Detail>
          <Detail label="Telefon">
            {contact.phone ? (
              <a
                className="inline-flex items-center gap-1.5 hover:underline"
                href={`tel:${contact.phone}`}
              >
                <Phone className="h-3.5 w-3.5" />
                {contact.phone}
              </a>
            ) : (
              '—'
            )}
          </Detail>
        </dl>

        {contact.notes && (
          <div className="mt-6 rounded-md bg-muted/50 p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Notiz
            </p>
            <p className="mt-1.5 whitespace-pre-wrap text-sm">{contact.notes}</p>
          </div>
        )}
      </Card>

      {offeneAufgaben.length > 0 && (
        <Card className="p-6">
          <h2 className="text-sm font-semibold">Offene Aufgaben</h2>
          <ul className="mt-3 space-y-2">
            {offeneAufgaben.map((t) => (
              <li key={t.id} className="flex items-center gap-3 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={false}
                  aria-label={`${t.title} erledigen`}
                  onChange={() => {
                    void crm.tasks.setStatus(t.id, 'erledigt').then(load);
                  }}
                />
                <span className="flex-1">{t.title}</span>
                {t.dueAt && (
                  <span className="text-xs text-muted-foreground">
                    {new Date(t.dueAt).toLocaleDateString('de-DE')}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {termine.length > 0 && (
        <Card className="p-6">
          <h2 className="text-sm font-semibold">Termine</h2>
          <ul className="mt-3 divide-y">
            {termine.map((e) => (
              <li key={e.id} className="flex items-baseline gap-3 py-2.5 text-sm">
                <time
                  className="w-32 shrink-0 text-xs text-muted-foreground"
                  dateTime={e.startAt}
                >
                  {new Date(e.startAt).toLocaleString('de-DE', {
                    dateStyle: 'short',
                    ...(e.allDay ? {} : { timeStyle: 'short' }),
                  })}
                </time>
                <span className="flex-1">{e.title || '(ohne Titel)'}</span>
                {e.location && (
                  <span className="text-xs text-muted-foreground">{e.location}</span>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {orders.length > 0 && (
        <Card className="p-6">
          <h2 className="text-sm font-semibold">Aufträge</h2>
          <ul className="mt-3 divide-y">
            {orders.map((o) => (
              <li key={o.id} className="flex items-center gap-3 py-2.5 text-sm">
                <span className="font-mono text-xs text-muted-foreground">
                  {o.number}
                </span>
                <button
                  className="flex-1 text-left hover:underline"
                  onClick={() => navigate('/projekte')}
                >
                  {o.title}
                </button>
                {o.totalTasks > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {o.totalTasks - o.openTasks}/{o.totalTasks} erledigt
                  </span>
                )}
                {o.valueCents > 0 && (
                  <span className="text-xs">{formatEuro(o.valueCents)}</span>
                )}
                <OrderStatusPill status={o.status as never} />
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card className="p-6">
        <h2 className="text-sm font-semibold">Schriftverkehr</h2>
        {threads.length === 0 && freieEntwuerfe.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">
            Noch keine Nachrichten. Sie erscheinen hier automatisch, sobald das
            Postfach abgerufen wurde.
          </p>
        ) : (
          <ul className="mt-3 divide-y">
            {threads.map((t) => (
              <li key={t.threadId}>
                <button
                  className="w-full py-3 text-left hover:bg-muted/30"
                  onClick={() => void openThread(t.threadId)}
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <span
                      className={`truncate text-sm ${t.unseen > 0 ? 'font-semibold' : ''}`}
                    >
                      {t.lastDirection === 'outbound' && (
                        <span className="mr-1 text-muted-foreground">↗</span>
                      )}
                      {t.subject || '(ohne Betreff)'}
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {t.lastAt ? new Date(t.lastAt).toLocaleDateString('de-DE') : ''}
                      {t.messageCount > 1 && ` · ${t.messageCount}`}
                    </span>
                  </div>
                  {t.snippet && (
                    <p className="mt-0.5 truncate text-xs text-muted-foreground">
                      {t.snippet}
                    </p>
                  )}
                </button>

                {openThreadId === t.threadId && (
                  <div className="space-y-3 pb-4">
                    {threadMessages.map((m) => (
                      <div key={m.id} className="space-y-2">
                        <article className="rounded-md border p-3">
                          <header className="flex flex-wrap items-baseline justify-between gap-2 text-xs text-muted-foreground">
                            <span>
                              {m.direction === 'outbound' ? 'Von uns' : m.fromAddress}
                            </span>
                            <time dateTime={m.receivedAt ?? undefined}>
                              {m.receivedAt
                                ? new Date(m.receivedAt).toLocaleString('de-DE', {
                                    dateStyle: 'short',
                                    timeStyle: 'short',
                                  })
                                : ''}
                            </time>
                          </header>
                          <ErstanspracheHinweis
                            message={m}
                            onChanged={async () => {
                              if (openThreadId)
                                setThreadMessages(await crm.mailbox.thread(openThreadId));
                              await load();
                            }}
                          />
                          <p className="mt-1.5 whitespace-pre-wrap text-sm">
                            {m.body ?? m.snippet ?? '(kein Text übertragen)'}
                          </p>
                          <div className="mt-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setReplying(m)}
                            >
                              Antworten
                            </Button>
                          </div>
                        </article>

                        {drafts
                          .filter((d) => d.replyToMessageId === m.id)
                          .map((d) => (
                            <DraftCard
                              key={d.id}
                              draft={d}
                              onOpen={setOffenerEntwurf}
                              onDiscarded={async () => {
                                if (openThreadId)
                                  setDrafts(await crm.drafts.list({ threadId: openThreadId }));
                              }}
                            />
                          ))}
                      </div>
                    ))}

                    {/*
                      Ein Entwurf ohne Bezugsnachricht stand früher hier — er
                      kam nie an: Diese Liste ist nach `threadId` geholt, und
                      einen Thread hat nur, wer eine Bezugsmail hat. Er steht
                      jetzt unten am Verlauf, außerhalb der Unterhaltungen.
                    */}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        {freieEntwuerfe.length > 0 && (
          <div className="mt-4 space-y-3 border-t pt-4">
            <p className="text-xs text-muted-foreground">
              Vorbereitet, ohne Bezug auf eine Unterhaltung
            </p>
            {freieEntwuerfe.map((d) => (
              // Ohne Bezugsmail gibt es nichts zu beantworten — die DraftCard
              // zeigt dafür kein „Öffnen & Senden“. Bleibt: lesen oder
              // verwerfen.
              <DraftCard
                key={d.id}
                draft={d}
                onOpen={setOffenerEntwurf}
                onDiscarded={() => void ladeFreieEntwuerfe()}
              />
            ))}
          </div>
        )}
      </Card>

      <Card className="p-6">
        <h2 className="text-sm font-semibold">Verlauf</h2>
        {activities.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">
            Noch nichts passiert.
          </p>
        ) : (
          <ol className="mt-3 space-y-3">
            {activities.map((a) => (
              <li key={a.id} className="flex gap-3 text-sm">
                <time
                  className="w-32 shrink-0 text-xs text-muted-foreground"
                  dateTime={a.createdAt}
                >
                  {new Date(a.createdAt).toLocaleString('de-DE', {
                    dateStyle: 'short',
                    timeStyle: 'short',
                  })}
                </time>
                <span>{a.summary}</span>
              </li>
            ))}
          </ol>
        )}
      </Card>

      {editing && (
        <ContactDialog
          contact={contact}
          audiences={audiences}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            void load();
          }}
        />
      )}

      {firmaWaehlen && (
        <SetOrganizationDialog
          contact={contact}
          audiences={audiences}
          onClose={() => setFirmaWaehlen(false)}
          onChanged={() => {
            setFirmaWaehlen(false);
            void load();
          }}
        />
      )}

      {promoting && (
        <PromoteCustomerDialog
          contact={contact}
          onClose={() => setPromoting(false)}
          onDone={() => {
            setPromoting(false);
            void load();
          }}
        />
      )}

      {deleting && (
        <DeleteContactDialog
          contact={contact}
          onClose={() => setDeleting(false)}
          onDeleted={() => {
            // Dorthin zurueck, woher man kam — die vorige Seite ist eine Liste
            // oder eine Kampagne, nie diese Akte selbst.
            zurueck();
          }}
        />
      )}

      {replying && (
        <ReplyDialog
          message={replying}
          onClose={() => setReplying(null)}
          onSent={async () => {
            setReplying(null);
            if (openThreadId) setThreadMessages(await crm.mailbox.thread(openThreadId));
            await load();
          }}
        />
      )}

      {offenerEntwurf && offenerEntwurfBezug && (
        <ReplyDialog
          message={offenerEntwurfBezug}
          initialSubject={offenerEntwurf.subject}
          initialBody={offenerEntwurf.body}
          draftId={offenerEntwurf.id}
          onClose={() => setOffenerEntwurf(null)}
          onSent={async () => {
            setOffenerEntwurf(null);
            if (openThreadId) {
              setThreadMessages(await crm.mailbox.thread(openThreadId));
              setDrafts(await crm.drafts.list({ threadId: openThreadId }));
            }
          }}
        />
      )}

      {composing && contact.email && (
        <MailComposer
          contact={contact}
          onClose={() => {
            setComposing(false);
            void load();
          }}
        />
      )}
    </div>
  );
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-1 text-sm">{children}</dd>
    </div>
  );
}

/**
 * Weitere Adressen, unter denen derselbe Mensch schreibt.
 *
 * Versendet wird immer an die Hauptadresse — diese hier sind nur zum
 * Wiedererkennen eingehender Post. Ohne sie strandet die Antwort von
 * `sekretariat@` auf eine Erstansprache an `info@` bei der Firma, und es wird
 * munter weiter nachgefasst.
 */
function Zweitadressen({ contactId }: { contactId: string }) {
  const [adressen, setAdressen] = useState<ContactEmail[] | null>(null);
  const [neu, setNeu] = useState('');
  const [offen, setOffen] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const laden = useCallback(() => {
    void crm.contacts.emails(contactId).then(setAdressen);
  }, [contactId]);
  useEffect(laden, [laden]);

  const hinzufuegen = async () => {
    const adresse = neu.trim();
    if (!adresse) return;
    setBusy(true);
    setFehler(null);
    try {
      await crm.contacts.addEmail(contactId, adresse);
      setNeu('');
      setOffen(false);
      laden();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Hinzufügen fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  if (!adressen) return null;

  return (
    <div className="mt-1.5 space-y-1">
      {adressen.map((a) => (
        <div key={a.id} className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <a className="hover:underline" href={`mailto:${a.email}`}>
            {a.email}
          </a>
          <button
            type="button"
            aria-label={`${a.email} entfernen`}
            title="Entfernen"
            className="text-muted-foreground hover:text-destructive"
            onClick={() => {
              void crm.contacts.removeEmail(a.id).then(laden);
            }}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}

      {offen ? (
        <div className="flex items-center gap-1.5">
          <Input
            autoFocus
            className="h-8 max-w-[16rem] text-sm"
            placeholder="sekretariat@…"
            value={neu}
            onChange={(e) => setNeu(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void hinzufuegen();
              if (e.key === 'Escape') {
                setOffen(false);
                setFehler(null);
              }
            }}
          />
          <Button size="sm" disabled={busy} onClick={() => void hinzufuegen()}>
            Merken
          </Button>
        </div>
      ) : (
        <button
          type="button"
          className="text-xs text-brand hover:underline"
          onClick={() => setOffen(true)}
        >
          + Weitere Adresse
        </button>
      )}

      {fehler && <p className="text-xs text-danger">{fehler}</p>}
    </div>
  );
}
